import React, { useMemo, useState } from "react";
import {
  Grid,
  Box,
  Button,
  Card,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Divider,
} from "@mui/material";
import {
  useGetNotificationQuery,
  useMarkNotificationAsReadMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import useIsMobile from "../../../Core/hook/useIsMobile";
import Pagination from "../../../Components/Pagination";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import NotificationsIcon from "@mui/icons-material/Notifications";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const Index = () => {
  const classes = useStyles();
  const { showErrorMessage, showMessage } = useNotifier();
  const isMobileRes = useIsMobile();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [typeFilter, setTypeFilter] = useState("");

  const { data, isLoading, isError } = useGetNotificationQuery({
    type: typeFilter,
  });

  const [markAsRead] = useMarkNotificationAsReadMutation();

  const notifications: any[] = useMemo(() => Array.isArray(data?.data) ? (data?.data as any[]) : [], [data]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      const response = await markAsRead({ notificationId }).unwrap();
      if (response.status) {
        showMessage("Marked as read");
      }
    } catch (error) {
      showErrorMessage("Failed to mark as read");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await markAsRead({ markAll: true }).unwrap();
      if (response.status) {
        showMessage("All notifications marked as read");
      }
    } catch (error) {
      showErrorMessage("Failed to mark all as read");
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case "info": return <InfoIcon color="info" />;
      case "warning": return <WarningIcon color="warning" />;
      case "error": return <ErrorIcon color="error" />;
      case "success": return <CheckCircleIcon color="success" />;
      default: return <NotificationsIcon color="primary" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case "info": return "info";
      case "warning": return "warning";
      case "error": return "error";
      case "success": return "success";
      default: return "default";
    }
  };

  return (
    <Card className={classes.card}>
      <Grid
        container
        justifyContent={"space-between"}
        spacing={2}
        sx={{ padding: 2 }}
        className={classes.titleGrid}
      >
        <Grid item lg={6} md={6} sm={6} xs={6}>
          <Typography className={classes.cardTitle}>Notifications</Typography>
        </Grid>
        <Grid item lg={6} md={6} sm={6} xs={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            startIcon={<DoneAllIcon />}
            onClick={handleMarkAllAsRead}
            size="small"
          >
            Mark All as Read
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ padding: 2 }}>
        <Grid item lg={3} md={4} sm={6} xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel>Type</InputLabel>
            <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="schedule">Schedule</MenuItem>
              <MenuItem value="payment">Payment</MenuItem>
              <MenuItem value="attendance">Attendance</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid sx={{ padding: 2 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="20vh">
            <CircularProgress />
          </Box>
        ) : isError || notifications.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="20vh">
            <Typography color="textSecondary">
              {isError ? "Error fetching notifications" : "No notifications"}
            </Typography>
          </Box>
        ) : (
          notifications
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((notification: any, index: number) => (
              <Box key={notification.id}>
                <Card
                  sx={{
                    p: 2,
                    mb: 1,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    backgroundColor: notification.isRead === false ? "action.hover" : "transparent",
                    border: notification.isRead === false ? "1px solid" : "none",
                    borderColor: "primary.light",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "action.selected" },
                  }}
                  onClick={() => {
                    if (!notification.isRead) {
                      handleMarkAsRead(notification.id?.toString());
                    }
                  }}
                >
                  <Box sx={{ mt: 0.5 }}>
                    {getNotificationIcon(notification.type)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid item>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: notification.isRead === false ? "bold" : "normal" }}
                        >
                          {notification.title || "Notification"}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Grid container spacing={1} alignItems="center">
                          {notification.type && (
                            <Grid item>
                              <Chip
                                label={notification.type}
                                color={getTypeColor(notification.type) as any}
                                size="small"
                                variant="outlined"
                              />
                            </Grid>
                          )}
                          <Grid item>
                            <Typography variant="caption" color="textSecondary">
                              {notification.createdAt ? dayjs(notification.createdAt).fromNow() : ""}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                      {notification.message || notification.body || ""}
                    </Typography>
                  </Box>
                  {notification.isRead === false && (
                    <Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification.id?.toString());
                        }}
                        title="Mark as read"
                      >
                        <DoneAllIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Card>
              </Box>
            ))
        )}
      </Grid>

      <Grid spacing={5} p={"18px"} py={4} justifyContent={"end"} container textAlign={"center"} alignContent={"center"} alignItems={"center"}>
        <Grid sx={{ textAlign: "end", justifyContent: "flex-end", alignItems: "flex-end" }}>
          <Pagination count={notifications.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} />
        </Grid>
      </Grid>
    </Card>
  );
};

export default Index;
